import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Product, ProductCategory, ProductVariant, BulkTier } from '@runninglime/shared';
import { apiGet, apiPost, apiPut, apiUpload, apiDelete } from '../../lib/api.ts';
import { LoadingSpinner } from '../../components/ui/Shared.tsx';

const CATEGORIES: ProductCategory[] = ['soda', 'energy', 'brainstorm', 'hydrate', 'cold-brew'];

function emptyVariant(): ProductVariant {
  return { size: '', unit: '', pricePerUnit: 0, minOrderQty: 1, bulkPricing: [] };
}

function emptyTier(): BulkTier {
  return { minQty: 0, pricePerUnit: 0 };
}

export default function ProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ProductCategory>('soda');
  const [variants, setVariants] = useState<ProductVariant[]>([emptyVariant()]);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingProduct, setUploadingProduct] = useState(false);
  const [uploadingVariant, setUploadingVariant] = useState<number | null>(null);
  const productFileRef = useRef<HTMLInputElement>(null);
  const variantFileRefs = useRef<(HTMLInputElement | null)[]>([]);

  const API_BASE = import.meta.env.VITE_API_BASE || '/api';

  useEffect(() => {
    if (!isNew && id) {
      apiGet<Product>(`/products/${id}`).then((res) => {
        if (res.success && res.data) {
          setName(res.data.name);
          setDescription(res.data.description);
          setCategory(res.data.category);
          setVariants(res.data.variants);
          setImageUrl(res.data.imageUrl || '');
        }
        setLoading(false);
      });
    }
  }, [id, isNew]);

  const updateVariant = (idx: number, partial: Partial<ProductVariant>) => {
    setVariants((v) => v.map((item, i) => (i === idx ? { ...item, ...partial } : item)));
  };

  const addVariant = () => setVariants((v) => [...v, emptyVariant()]);
  const removeVariant = (idx: number) => setVariants((v) => v.filter((_, i) => i !== idx));

  const addTier = (vidx: number) => {
    setVariants((v) => v.map((item, i) =>
      i === vidx ? { ...item, bulkPricing: [...(item.bulkPricing || []), emptyTier()] } : item
    ));
  };

  const updateTier = (vidx: number, tidx: number, partial: Partial<BulkTier>) => {
    setVariants((v) => v.map((item, i) =>
      i === vidx ? {
        ...item,
        bulkPricing: (item.bulkPricing || []).map((t: BulkTier, j: number) => (j === tidx ? { ...t, ...partial } : t)),
      } : item
    ));
  };

  const removeTier = (vidx: number, tidx: number) => {
    setVariants((v) => v.map((item, i) =>
      i === vidx ? {
        ...item,
        bulkPricing: (item.bulkPricing || []).filter((_: BulkTier, j: number) => j !== tidx),
      } : item
    ));
  };

  // ─── Image Handlers ──────────────────────────────────────────────────────

  const resolveImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_BASE.replace(/\/api\/?$/, '')}${url}`;
  };

  const handleProductImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    setUploadingProduct(true);
    const res = await apiUpload<{ imageUrl: string }>(`/admin/products/${id}/image`, file);
    if (res.success && res.data) {
      setImageUrl(res.data.imageUrl);
    }
    setUploadingProduct(false);
    if (productFileRef.current) productFileRef.current.value = '';
  };

  const removeProductImage = async () => {
    if (isNew) return;
    setUploadingProduct(true);
    await apiDelete(`/admin/products/${id}/image`);
    setImageUrl('');
    setUploadingProduct(false);
  };

  const handleVariantImage = async (vi: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isNew) return;
    setUploadingVariant(vi);
    const res = await apiUpload<{ imageUrl: string }>(`/admin/products/${id}/variants/${vi}/image`, file);
    if (res.success && res.data) {
      updateVariant(vi, { imageUrl: res.data.imageUrl });
    }
    setUploadingVariant(null);
    if (variantFileRefs.current[vi]) variantFileRefs.current[vi]!.value = '';
  };

  const removeVariantImage = async (vi: number) => {
    if (isNew) return;
    setUploadingVariant(vi);
    await apiDelete(`/admin/products/${id}/variants/${vi}/image`);
    updateVariant(vi, { imageUrl: '' });
    setUploadingVariant(null);
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || variants.length === 0) {
      setError('Name and at least one variant are required');
      return;
    }
    for (const v of variants) {
      if (!v.size || !v.unit || v.pricePerUnit <= 0) {
        setError('All variant fields (size, unit, price) are required');
        return;
      }
    }
    setSaving(true);
    const body = { name, description, category, variants };
    const res = isNew
      ? await apiPost('/admin/products', body)
      : await apiPut(`/admin/products/${id}`, body);
    if (res.success) {
      navigate('/admin/products');
    } else {
      setError(res.error || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="animate-fade-in max-w-3xl">
      <h1 className="text-3xl font-bold text-dark mb-6">{isNew ? 'New Product' : 'Edit Product'}</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm">{error}</div>}

        <div className="glass rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-dark">Details</h2>
          <div>
            <label className="block text-sm font-medium text-dark mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as ProductCategory)}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition bg-white"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dark mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-lime/50 focus:border-lime outline-none transition resize-none"
            />
          </div>
        </div>

        {/* Product Image */}
        {!isNew && (
          <div className="glass rounded-2xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-dark">Product Image</h2>
            <p className="text-xs text-gray-500">This image is shown as the product preview in the store.</p>
            {imageUrl ? (
              <div className="flex items-start gap-4">
                <img
                  src={resolveImageUrl(imageUrl)}
                  alt="Product"
                  className="w-32 h-32 object-cover rounded-xl border border-gray-200"
                />
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => productFileRef.current?.click()}
                    disabled={uploadingProduct}
                    className="text-sm text-green hover:underline disabled:opacity-50"
                  >
                    {uploadingProduct ? 'Uploading...' : 'Replace Image'}
                  </button>
                  <button
                    type="button"
                    onClick={removeProductImage}
                    disabled={uploadingProduct}
                    className="text-sm text-red-500 hover:underline disabled:opacity-50"
                  >
                    Remove Image
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => productFileRef.current?.click()}
                disabled={uploadingProduct}
                className="w-full border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-lime hover:bg-lime/5 transition-colors disabled:opacity-50"
              >
                <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                </svg>
                <span className="text-sm text-gray-500">
                  {uploadingProduct ? 'Uploading...' : 'Click to upload product image'}
                </span>
              </button>
            )}
            <input
              ref={productFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProductImage}
            />
          </div>
        )}

        {/* Variants */}
        <div className="glass rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-dark">Variants</h2>
            <button type="button" onClick={addVariant} className="text-sm text-green hover:underline">+ Add Variant</button>
          </div>
          <div className="space-y-4">
            {variants.map((v, vi) => (
              <div key={vi} className="border border-gray-200 rounded-xl p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-dark">Variant {vi + 1}</span>
                  {variants.length > 1 && (
                    <button type="button" onClick={() => removeVariant(vi)} className="text-xs text-red-500 hover:underline">Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Size</label>
                    <input
                      value={v.size}
                      onChange={(e) => updateVariant(vi, { size: e.target.value })}
                      placeholder="330ml"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-lime/50 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Unit</label>
                    <input
                      value={v.unit}
                      onChange={(e) => updateVariant(vi, { unit: e.target.value })}
                      placeholder="can"
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-lime/50 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Price (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={(v.pricePerUnit / 100).toFixed(2)}
                      onChange={(e) => updateVariant(vi, { pricePerUnit: Math.round(parseFloat(e.target.value || '0') * 100) })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-lime/50 outline-none"
                    />
                  </div>
                </div>
                {/* Bulk Tiers */}
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">Bulk Pricing Tiers</span>
                    <button type="button" onClick={() => addTier(vi)} className="text-xs text-green hover:underline">+ Add Tier</button>
                  </div>
                  {(v.bulkPricing || []).map((tier: BulkTier, ti: number) => (
                    <div key={ti} className="grid grid-cols-3 gap-2 mb-2 items-end">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Min Qty</label>
                        <input
                          type="number"
                          value={tier.minQty}
                          onChange={(e) => updateTier(vi, ti, { minQty: parseInt(e.target.value) || 0 })}
                          className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-lime/50 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Price (€)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={(tier.pricePerUnit / 100).toFixed(2)}
                          onChange={(e) => updateTier(vi, ti, { pricePerUnit: Math.round(parseFloat(e.target.value || '0') * 100) })}
                          className="w-full px-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:ring-2 focus:ring-lime/50 outline-none"
                        />
                      </div>
                      <button type="button" onClick={() => removeTier(vi, ti)} className="text-xs text-red-500 hover:underline py-1.5">Remove</button>
                    </div>
                  ))}
                </div>

                {/* Variant Image */}
                {!isNew && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="block text-xs text-gray-500 mb-2">Variant Image</label>
                    {v.imageUrl ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={resolveImageUrl(v.imageUrl)}
                          alt={`Variant ${vi + 1}`}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        />
                        <div className="flex flex-col gap-1">
                          <button
                            type="button"
                            onClick={() => variantFileRefs.current[vi]?.click()}
                            disabled={uploadingVariant === vi}
                            className="text-xs text-green hover:underline disabled:opacity-50"
                          >
                            {uploadingVariant === vi ? 'Uploading...' : 'Replace'}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeVariantImage(vi)}
                            disabled={uploadingVariant === vi}
                            className="text-xs text-red-500 hover:underline disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => variantFileRefs.current[vi]?.click()}
                        disabled={uploadingVariant === vi}
                        className="w-full border border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-lime hover:bg-lime/5 transition-colors text-xs text-gray-500 disabled:opacity-50"
                      >
                        {uploadingVariant === vi ? 'Uploading...' : '+ Add variant image'}
                      </button>
                    )}
                    <input
                      ref={(el) => { variantFileRefs.current[vi] = el; }}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleVariantImage(vi, e)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 btn-gradient py-3 rounded-xl font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving...' : isNew ? 'Create Product' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
